import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Inbox, Briefcase, Users, IndianRupee, Clock, ChevronRight, Award, ArrowRight, CheckCircle2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature 4 — small chip showing the campaign's response rate.
// Fetches the same /response-stats endpoint used by the CM analytics tab and
// renders nothing if the campaign has no invites yet (pre-conversion briefs).
const ResponseRateChip = ({ campaignId }) => {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/bulk/campaigns/${campaignId}/response-stats`, { withCredentials: true })
      .then((r) => { if (live && r.data?.invited > 0) setRate(r.data.response_rate); })
      .catch(() => {});
    return () => { live = false; };
  }, [campaignId]);
  if (rate == null) return null;
  const color = rate >= 60 ? '#00C972' : rate >= 30 ? '#FFB800' : '#FF3B30';
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border"
      style={{ backgroundColor: `${color}15`, color, borderColor: `${color}40` }}
      data-testid={`admin-response-rate-chip-${campaignId}`}
      title={`${rate}% response rate`}
    >
      {rate}% resp
    </span>
  );
};

export const AdminIncoming = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('pending'); // 'pending' | 'active'
  const [expandedActive, setExpandedActive] = useState(null);
  const [bulkConvertCampaign, setBulkConvertCampaign] = useState(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const [pending, active] = await Promise.all([
        axios.get(`${API_URL}/api/admin/incoming-campaigns`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/active-campaigns`, { withCredentials: true }),
      ]);
      setCampaigns(pending.data || []);
      setActiveCampaigns(active.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="admin-incoming-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <Inbox className="text-[#FF451A]" size={32} /> Campaigns Control
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Route incoming briefs and monitor live negotiations across the pipeline.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTab('pending')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'pending' ? 'bg-[#FFB800]/10 text-[#FFB800]' : 'text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`}
                data-testid="admin-tab-pending"
              >
                Pending Routing · {campaigns.length}
              </button>
              <button
                onClick={() => setTab('active')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'active' ? 'bg-[#00C972]/10 text-[#00C972]' : 'text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`}
                data-testid="admin-tab-active"
              >
                Active Pipeline · {activeCampaigns.length}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
            </div>
          ) : tab === 'pending' ? (
            campaigns.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
              <CheckCircle2 className="mx-auto text-[#00C972] mb-3" size={40} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">Inbox Zero! 🎉</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">No pending briefs. All caught up.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {campaigns.map((c) => (
                <div
                  key={c.id}
                  className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-5 flex items-center gap-4 hover:-translate-y-[1px] hover:border-[#FF451A]/30 transition-all"
                  data-testid="incoming-brief-card"
                >
                  <div onClick={() => setSelectedId(c.id)} className="flex-1 flex items-center gap-4 cursor-pointer min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-[#FFB800]/10 flex items-center justify-center text-[#FFB800]">
                      <Briefcase size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0F0F0F] dark:text-white text-lg flex items-center gap-2 flex-wrap">
                        {c.campaign_name}
                        {c.campaign_type === 'bulk' && (
                          <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-[#FF451A]/15 text-[#FF451A] border border-[#FF451A]/30" data-testid="brief-bulk-badge">
                            Bulk Campaign
                          </span>
                        )}
                        {c.reactivated_from && (
                          <span
                            className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/30 inline-flex items-center gap-1"
                            data-testid={`admin-reactivated-badge-${c.id}`}
                            title={`Reactivated from campaign ${c.reactivated_from}`}
                          >
                            <RotateCw size={9} /> Reactivated
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-1">
                        <span className="font-medium text-[#0F0F0F] dark:text-white">{c.brand_name}</span> • {(c.platforms || []).join(', ')}
                        {c.campaign_type === 'bulk' && c.expected_creator_count && (
                          <span> • Expected <span className="font-semibold text-[#0F0F0F] dark:text-white">{c.expected_creator_count}</span> creators</span>
                        )}
                        {c.created_at && (
                          <span> • {new Date(c.created_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right hidden md:block">
                      <div className="text-sm font-bold text-[#0F0F0F] dark:text-white flex items-center gap-1 justify-end">
                        <IndianRupee size={13} /> {(c.budget || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">{(c.shortlisted_creator_ids || []).length} shortlisted</div>
                    </div>
                    {c.exclusive_only && (
                      <span className="hidden md:flex px-2 py-1 rounded-full text-xs bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white items-center gap-1">
                        <Award size={11} /> Exclusive
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setBulkConvertCampaign(c); }}
                    className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-[#FF451A]/10 border border-[#FF451A]/30 text-[#FF451A] hover:bg-[#FF451A]/20 transition-colors shrink-0"
                    data-testid="convert-to-bulk-button"
                  >
                    Convert to Bulk
                  </button>
                  <ChevronRight size={20} className="text-[#6B6B6B] dark:text-zinc-500" onClick={() => setSelectedId(c.id)} />
                </div>
              ))}
            </div>
          )
          ) : (
            // Active Pipeline tab
            activeCampaigns.length === 0 ? (
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center" data-testid="admin-active-empty">
                <Inbox className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
                <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No active campaigns yet</h3>
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Routed briefs and live negotiations will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-active-list">
                {activeCampaigns.map((bc) => {
                  const isOpen = expandedActive === bc.id;
                  const statusColor = bc.status === 'completed' ? '#00C972' : bc.status === 'in_progress' ? '#0055FF' : '#FFB800';
                  return (
                    <div key={bc.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden" data-testid="admin-active-card">
                      <button
                        onClick={() => setExpandedActive(isOpen ? null : bc.id)}
                        className="w-full p-5 flex items-center gap-4 hover:bg-white/[0.03] transition-colors text-left"
                      >
                        <div className="w-12 h-12 rounded-xl bg-[#00C972]/10 flex items-center justify-center text-[#00C972]">
                          <Briefcase size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0F0F0F] dark:text-white text-lg truncate inline-flex items-center gap-2">
                            {bc.campaign_name}
                            {bc.reactivated_from && (
                              <span
                                className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/30 inline-flex items-center gap-1"
                                data-testid={`admin-reactivated-badge-${bc.id}`}
                                title={`Reactivated from campaign ${bc.reactivated_from}`}
                              >
                                <RotateCw size={9} /> Reactivated
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-[#0F0F0F] dark:text-white">{bc.brand_name}</span>
                            <span className="flex items-center gap-1"><IndianRupee size={11} />{(bc.budget || 0).toLocaleString()}</span>
                            <span>{bc.completed_count}/{bc.total_count} done</span>
                            {bc.negotiation_rounds > 0 && (
                              <span className="text-[#FFB800] font-mono">{bc.negotiation_rounds} negotiation round{bc.negotiation_rounds > 1 ? 's' : ''}</span>
                            )}
                            {bc.finalized_count > 0 && (
                              <span className="text-[#00C972] font-mono">{bc.finalized_count} finalized</span>
                            )}
                          </div>
                        </div>
                        <span
                          className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                          style={{ backgroundColor: `${statusColor}15`, color: statusColor }}
                        >
                          {(bc.status || '').replace('_', ' ')}
                        </span>
                        <ResponseRateChip campaignId={bc.id} />
                        <ChevronRight size={20} className={`text-[#6B6B6B] dark:text-zinc-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 border-t border-black/[0.08] dark:border-white/5 bg-[#050505]/40">
                          {(bc.child_progress || []).length === 0 ? (
                            <div className="py-4 text-sm text-[#6B6B6B] dark:text-zinc-500 italic">No creator assignments yet.</div>
                          ) : (
                            <div className="space-y-1.5 mt-3">
                              {bc.child_progress.map((cp, i) => {
                                const stageColor = { pitched: '#0055FF', outreach: '#9B6EFF', locked: '#00C972', live: '#FF451A', completed: '#666666', declined: '#FF3B30' }[cp.stage] || '#999';
                                const fmt = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
                                const hasNego = cp.negotiation_round_count > 0 || cp.latest_counter_offer != null || cp.finalized_rate != null;
                                return (
                                  <div key={i} className="p-2.5 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg" data-testid="admin-active-row">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold shrink-0">
                                          {cp.creator_name?.[0]?.toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{cp.creator_name}</div>
                                          <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500">via {cp.manager_name}</div>
                                        </div>
                                      </div>
                                      <span
                                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0"
                                        style={{ backgroundColor: `${stageColor}15`, color: stageColor }}
                                      >
                                        {cp.stage.replace('_', ' ')}
                                      </span>
                                    </div>
                                    {hasNego && (
                                      <div className="mt-2 pl-9 flex flex-wrap items-center gap-1.5" data-testid="admin-negotiation-chips">
                                        {cp.latest_brand_offer != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border border-black/[0.10] dark:border-white/10">
                                            offer {fmt(cp.latest_brand_offer)}
                                          </span>
                                        )}
                                        {cp.latest_counter_offer != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20">
                                            counter {fmt(cp.latest_counter_offer)}
                                          </span>
                                        )}
                                        {cp.finalized_rate != null && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/20 font-semibold">
                                            final {fmt(cp.finalized_rate)}
                                          </span>
                                        )}
                                        {cp.negotiation_round_count > 0 && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-white/5 text-[#6B6B6B] dark:text-zinc-500 border border-black/[0.10] dark:border-white/10">
                                            {cp.negotiation_round_count} round{cp.negotiation_round_count > 1 ? 's' : ''}
                                          </span>
                                        )}
                                        {cp.last_negotiation_by && (
                                          <span className="text-[10px] font-mono text-[#A1A1AA] dark:text-zinc-600">· by {cp.last_negotiation_by}</span>
                                        )}
                                      </div>
                                    )}
                                    {cp.last_negotiation_note && (
                                      <div className="mt-1.5 pl-9 text-[11px] text-[#6B6B6B] dark:text-zinc-500 italic truncate">"{cp.last_negotiation_note}"</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {selectedId && (
            <AssignDialog
              campaignId={selectedId}
              onClose={() => setSelectedId(null)}
              onAssigned={() => {
                setSelectedId(null);
                fetchCampaigns();
              }}
            />
          )}

          {bulkConvertCampaign && (
            <ConvertToBulkDialog
              campaign={bulkConvertCampaign}
              onClose={() => setBulkConvertCampaign(null)}
              onDone={() => { setBulkConvertCampaign(null); fetchCampaigns(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const AssignDialog = ({ campaignId, onClose, onAssigned }) => {
  const [campaign, setCampaign] = useState(null);
  const [talentManagers, setTalentManagers] = useState([]);
  const [campaignManagers, setCampaignManagers] = useState([]);
  const [assignments, setAssignments] = useState({}); // creatorId -> managerId
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [det, tm, cm] = await Promise.all([
          axios.get(`${API_URL}/api/admin/campaign/${campaignId}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/users/managers?type=talent`, { withCredentials: true }),
          axios.get(`${API_URL}/api/users/managers?type=campaign`, { withCredentials: true }),
        ]);
        setCampaign(det.data);
        setTalentManagers(tm.data || []);
        setCampaignManagers(cm.data || []);

        // Auto-suggest: prefer the creator's already-assigned TM, fallback to round-robin
        const auto = {};
        let useTm = 0, useCm = 0;
        (det.data?.creator_details || []).forEach((cr) => {
          if (cr.exclusivity_type === 'exclusive' && cr.assigned_talent_manager_id) {
            const match = (tm.data || []).find((m) => m.id === cr.assigned_talent_manager_id);
            if (match) { auto[cr.id] = match.id; return; }
          }
          const pool = cr.exclusivity_type === 'exclusive' ? (tm.data || []) : (cm.data || []);
          if (pool.length === 0) return;
          if (cr.exclusivity_type === 'exclusive') { auto[cr.id] = pool[useTm % pool.length].id; useTm++; }
          else { auto[cr.id] = pool[useCm % pool.length].id; useCm++; }
        });
        setAssignments(auto);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load campaign details');
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  const handleAssign = async () => {
    const entries = Object.entries(assignments).filter(([_, m]) => m);
    if (entries.length === 0) {
      toast.error('Please assign at least one creator to a manager');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/campaign/${campaignId}/assign`,
        {
          assignments: entries.map(([creator_id, manager_id]) => ({ creator_id, manager_id })),
        },
        { withCredentials: true }
      );
      toast.success('Campaign assigned successfully!');
      onAssigned();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  const setAuto = () => {
    if (!campaign?.creator_details) return;
    const auto = {};
    campaign.creator_details.forEach((c) => {
      const pool = c.exclusivity_type === 'exclusive' ? talentManagers : campaignManagers;
      if (pool.length > 0) {
        // Round-robin
        const idx = Object.keys(auto).length % pool.length;
        auto[c.id] = pool[idx].id;
      }
    });
    setAssignments(auto);
    toast.success('Auto-assigned based on exclusivity rules');
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">Assign Campaign</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
          {campaign && (
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              <span className="font-medium text-[#0F0F0F] dark:text-white">{campaign.campaign_name}</span> from {campaign.brand_name}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        ) : campaign ? (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Budget" value={`₹${(campaign.budget || 0).toLocaleString()}`} />
              <Stat label="Creators" value={campaign.creator_count} />
              <Stat label="Shortlisted" value={(campaign.shortlisted_creator_ids || []).length} />
              <Stat label="Exclusive Only" value={campaign.exclusive_only ? 'Yes' : 'No'} />
            </div>

            <div className="p-4 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl">
              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 mb-1">Brief</div>
              <p className="text-sm text-[#0F0F0F] dark:text-white">{campaign.brief}</p>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0F0F0F] dark:text-white">Assign Creators to Managers</h3>
              <button
                onClick={setAuto}
                className="text-xs text-[#FF451A] hover:text-[#E63E17] font-medium"
                data-testid="auto-assign-button"
              >
                Auto-assign by exclusivity
              </button>
            </div>

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
              {(campaign.creator_details || []).map((c) => {
                const pool = c.exclusivity_type === 'exclusive' ? talentManagers : campaignManagers;
                return (
                  <div key={c.id} className="flex items-center gap-3 p-3 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white font-bold shrink-0">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 flex items-center gap-1.5">
                      {c.exclusivity_type === 'exclusive' ? (
                        <><Award size={10} /> Exclusive (Talent Manager)</>
                      ) : (
                        'Marketplace (Campaign Manager)'
                      )}
                    </div>
                    {c.exclusivity_type === 'exclusive' && c.assigned_talent_manager_id && (() => {
                      const tm = talentManagers.find((m) => m.id === c.assigned_talent_manager_id);
                      return tm ? (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[#00C972] mt-0.5">
                          🔗 Already managed by {tm.name}
                        </div>
                      ) : null;
                    })()}
                  </div>
                    <ArrowRight size={14} className="text-[#6B6B6B] dark:text-zinc-500" />
                    <div className="w-48">
                      <Select
                        value={assignments[c.id] || ''}
                        onValueChange={(v) => setAssignments((a) => ({ ...a, [c.id]: v }))}
                      >
                        <SelectTrigger className="rounded-lg text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid={`manager-select-${c.id}`}>
                          <SelectValue placeholder="Select manager..." />
                        </SelectTrigger>
                        <SelectContent>
                          {pool.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-[#6B6B6B] dark:text-zinc-500">No managers available</div>
                          ) : (
                            pool.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <DialogFooter className="mt-4">
          <Button onClick={onClose} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6">
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={submitting}
            className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 hover:bg-[#E63E17] disabled:opacity-50"
            data-testid="confirm-assign-button"
          >
            {submitting ? 'Assigning...' : 'Confirm & Notify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-3">
    <div className="text-[10px] uppercase text-[#6B6B6B] dark:text-zinc-500 tracking-wider">{label}</div>
    <div className="font-semibold text-[#0F0F0F] dark:text-white">{value}</div>
  </div>
);


// Admin → convert incoming brief into a bulk campaign + assign one or more
// Campaign Managers. This is the one path that creates a bulk workspace.
const ConvertToBulkDialog = ({ campaign, onClose, onDone }) => {
  const [cost, setCost] = useState(1500);
  const [notes, setNotes] = useState('');
  const [campaignManagers, setCampaignManagers] = useState([]);
  const [pickedManagerIds, setPickedManagerIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/users/managers?type=campaign`, { withCredentials: true });
        setCampaignManagers(data || []);
      } catch (e) {
        toast.error('Failed to load campaign managers');
      }
    })();
  }, []);

  const submit = async () => {
    if (!cost || cost <= 0) { toast.error('Set a fixed cost per creator'); return; }
    if (pickedManagerIds.length === 0) { toast.error('Pick at least one Campaign Manager'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/bulk/campaigns/${campaign.id}/convert`,
        { fixed_cost_per_creator: Number(cost), notes: notes.trim() || null },
        { withCredentials: true }
      );
      await axios.post(
        `${API_URL}/api/bulk/campaigns/${campaign.id}/assign-managers`,
        { campaign_manager_ids: pickedManagerIds },
        { withCredentials: true }
      );
      // Seed bulk_creators from the shortlist that came with the brief
      if ((campaign.shortlisted_creator_ids || []).length > 0) {
        await axios.post(
          `${API_URL}/api/bulk/campaigns/${campaign.id}/creators`,
          { creator_ids: campaign.shortlisted_creator_ids },
          { withCredentials: true }
        );
      }
      toast.success('Converted to bulk campaign');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePick = (mid) => {
    setPickedManagerIds((prev) => prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid]);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convert to Bulk Campaign</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="convert-to-bulk-dialog">
          <div>
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">Campaign</div>
            <div className="text-[#0F0F0F] dark:text-white font-semibold">{campaign.campaign_name} · {campaign.brand_name}</div>
            <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 mt-0.5">{(campaign.shortlisted_creator_ids || []).length} creators will seed the pipeline as 'invited'</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Fixed cost per creator (₹)</label>
            <input
              type="number"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="convert-bulk-cost-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Campaign Managers</label>
            <div className="max-h-40 overflow-y-auto border border-black/[0.12] dark:border-white/[0.08] rounded-xl bg-white dark:bg-[#1A1D24]" data-testid="convert-bulk-cm-list">
              {campaignManagers.length === 0 ? (
                <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 p-3">No campaign managers found.</div>
              ) : campaignManagers.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/5 cursor-pointer">
                  <input type="checkbox" checked={pickedManagerIds.includes(m.id)} onChange={() => togglePick(m.id)} className="accent-[#FF451A]" data-testid="convert-bulk-cm-checkbox" />
                  <span className="text-sm text-[#0F0F0F] dark:text-white">{m.name}</span>
                  <span className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 ml-auto font-mono">{m.role}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-xl px-3 py-2 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="convert-bulk-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 hover:bg-[#E63E17] disabled:opacity-50" data-testid="confirm-bulk-convert-button">
            {submitting ? 'Converting…' : 'Convert + Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
